import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import { toast } from 'react-toastify';

import { fetchWrapper } from '../../../helpers';
import { getUtcOffset } from '../../../utils/common';
import { getSerializedErrorMessage } from '../../../utils/errors';
import { buildQuestionnaireHistoryUrl, redirectToDashboardOnClientError } from '../utils';
import { ONBOARDING_QUESTIONNAIRES_API_BASE } from './constants';
import { patchOnboardingQuestionnaireConcludeFields } from './utils';
import { addOnboardingAgreementCases, createOnboardingAgreementThunks } from './pages/agreement';
import {
  addOnboardingDocumentsCases,
  createOnboardingDocumentsThunks,
  mergeOnboardingQuestionnaireDocumentsPreservingUrls,
} from './pages/documents';
import { addOnboardingGeneralCases, createOnboardingGeneralThunks } from './pages/general';
import {
  addOnboardingInsuranceCases,
  createOnboardingInsuranceReducers,
  createOnboardingInsuranceThunks,
} from './pages/insurance';
import { addOnboardingReferralCases, createOnboardingReferralThunks } from './pages/referral';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type {
  IOnboardingQuestionnaireDetails,
  OnboardingTabStatusMap,
} from '../../../pages/questionnaires/onboarding/types';
import type {
  GetOnboardingQuestionnaireByIdPayload,
  GetOnboardingQuestionnairesByProviderIdPayload,
  GetOnboardingQuestionnairesPayload,
  IOnboardingQuestionnaireBasic,
  OnboardingQuestionnaireState,
  SendOnboardingQuestionnairePayload,
  SubmitOnboardingQuestionnairePayload,
  UpdateOnboardingTabStatusPayload,
} from './types';

const initialState = createInitialState();
const reducers = createReducers();
const extraActions = createExtraActions();
const slice = createSlice({
  name: 'onboardingQuestionnaire',
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<OnboardingQuestionnaireState>) =>
    createExtraReducers(builder),
});

function createInitialState(): OnboardingQuestionnaireState {
  const tabStatuses: OnboardingTabStatusMap = {
    introduction: { error: false, success: false },
    general: { error: false, success: false },
    'primary-insurance': { error: false, success: false },
    'secondary-insurance': { error: false, success: false },
    referral: { error: false, success: false },
    agreement: { error: false, success: false },
    submit: { error: false, success: false },
  };
  return {
    tabStatuses,
    error: null,
    uploadDocumentLoading: false,
    documentDetailsLoading: false,
    updateAgreementLoading: false,
    deleteDocumentLoading: false,
    pendingDocumentDetailsLoadingCount: 0,
    onboardingQuestionnairesBasicInfoLoading: false,
    onboardingQuestionnairesBasicInfoRequestId: null,
    onboardingQuestionnairesBasicInfoProviderId: null,
    questionnaireDetailsLoading: false,
    questionnaireDetailsRequestId: null,
    sendQuestionnaireLoading: false,
    submitQuestionnaireLoading: false,
    updateGeneralInfoLoading: false,
    upsertPrimaryInsuranceLoading: false,
    upsertSecondaryInsuranceLoading: false,
    deleteSecondaryInsuranceLoading: false,
    noSecondaryInsuranceLoading: false,
    updateReferralInfoLoading: false,
    pendingGeneralInfoUpdateCount: 0,
    pendingReferralInfoUpdateCount: 0,
    onboardingQuestionnairesBasicInfo: [],
    onboardingQuestionnaireDetails: null,
    onboardingQuestionnaires: [],
    onboardingQuestionnairesLoading: false,
    insuranceExtraction: {
      PRIMARY: null,
      SECONDARY: null,
    },
  };
}

function createReducers() {
  return {
    updateOnboardingTabStatus,
    ...createOnboardingInsuranceReducers(),
    clearOnboardingQuestionnaires,
    clearOnboardingQuestionnairesBasicInfo,
  };

  function clearOnboardingQuestionnaires(state: OnboardingQuestionnaireState) {
    state.onboardingQuestionnaires = [];
    state.onboardingQuestionnairesLoading = false;
  }

  function clearOnboardingQuestionnairesBasicInfo(state: OnboardingQuestionnaireState) {
    state.onboardingQuestionnairesBasicInfo = [];
    state.onboardingQuestionnairesBasicInfoLoading = false;
    state.onboardingQuestionnairesBasicInfoRequestId = null;
    state.onboardingQuestionnairesBasicInfoProviderId = null;
  }

  function updateOnboardingTabStatus(
    state: OnboardingQuestionnaireState,
    action: PayloadAction<UpdateOnboardingTabStatusPayload>,
  ) {
    const { tabId, status } = action.payload;
    const currentStatus = state.tabStatuses[tabId];

    if (currentStatus.error === status.error && currentStatus.success === status.success) {
      return;
    }

    state.tabStatuses[tabId] = status;
  }
}

function createExtraActions() {
  return {
    ...createOnboardingQuestionnaireThunks(),
    ...createOnboardingGeneralThunks(),
    ...createOnboardingInsuranceThunks(),
    ...createOnboardingReferralThunks(),
    ...createOnboardingAgreementThunks(),
    ...createOnboardingDocumentsThunks(),
  };
}

function createOnboardingQuestionnaireThunks() {
  return {
    getOnboardingQuestionnairesByProviderId: getOnboardingQuestionnairesByProviderId(),
    getOnboardingQuestionnaires: getOnboardingQuestionnaires(),
    getOnboardingQuestionnaireById: getOnboardingQuestionnaireById(),
    sendOnboardingQuestionnaire: sendOnboardingQuestionnaire(),
    submitOnboardingQuestionnaire: submitOnboardingQuestionnaire(),
  };
}

function getOnboardingQuestionnairesByProviderId() {
  return createAsyncThunk<
    IOnboardingQuestionnaireBasic[],
    GetOnboardingQuestionnairesByProviderIdPayload
  >(
    'onboardingQuestionnaire/getOnboardingQuestionnairesByProviderId',
    async ({ providerId, getAccessTokenSilently }) =>
      // Filter the list endpoint by provider rather than calling `by_provider/{id}`: both
      // return the same rows, but only list populates the nested `client` summary that
      // consumers need for the client name. Passing a filter also opts out of the list
      // endpoint's default past-month window.
      fetchWrapper.get({
        url: buildQuestionnaireHistoryUrl(ONBOARDING_QUESTIONNAIRES_API_BASE, {
          provider_id: providerId,
        }),
        getAccessTokenSilently,
      }),
  );
}

function getOnboardingQuestionnaires() {
  return createAsyncThunk<IOnboardingQuestionnaireBasic[], GetOnboardingQuestionnairesPayload>(
    'onboardingQuestionnaire/getOnboardingQuestionnaires',
    async ({ params, getAccessTokenSilently, signal }) => {
      const response: IOnboardingQuestionnaireBasic[] = await fetchWrapper.get({
        url: buildQuestionnaireHistoryUrl(ONBOARDING_QUESTIONNAIRES_API_BASE, params),
        getAccessTokenSilently,
        signal,
      });
      return response;
    },
  );
}

function getOnboardingQuestionnaireById() {
  return createAsyncThunk<IOnboardingQuestionnaireDetails, GetOnboardingQuestionnaireByIdPayload>(
    'onboardingQuestionnaire/getOnboardingQuestionnaireById',
    async ({ onboardingQuestionnaireId, getAccessTokenSilently }) => {
      const response: IOnboardingQuestionnaireDetails = await fetchWrapper.get({
        url: `${ONBOARDING_QUESTIONNAIRES_API_BASE}/${onboardingQuestionnaireId}`,
        getAccessTokenSilently,
      });
      return response;
    },
  );
}

function sendOnboardingQuestionnaire() {
  return createAsyncThunk<IOnboardingQuestionnaireBasic, SendOnboardingQuestionnairePayload>(
    'onboardingQuestionnaire/sendOnboardingQuestionnaire',
    async ({ data, getAccessTokenSilently }) => {
      const response: IOnboardingQuestionnaireBasic = await fetchWrapper.post(
        {
          url: `${ONBOARDING_QUESTIONNAIRES_API_BASE}/`,
          getAccessTokenSilently,
        },
        {
          ...data,
          date_sent_utc_offset: getUtcOffset(moment()),
        },
      );
      return response;
    },
  );
}

function submitOnboardingQuestionnaire() {
  return createAsyncThunk<IOnboardingQuestionnaireBasic, SubmitOnboardingQuestionnairePayload>(
    'onboardingQuestionnaire/submitOnboardingQuestionnaire',
    async ({ onboardingQuestionnaireId, data, getAccessTokenSilently }) => {
      const response: IOnboardingQuestionnaireBasic = await fetchWrapper.post(
        {
          url: `${ONBOARDING_QUESTIONNAIRES_API_BASE}/${onboardingQuestionnaireId}/submit`,
          getAccessTokenSilently,
        },
        data,
      );
      return response;
    },
  );
}

function createExtraReducers(builder: ActionReducerMapBuilder<OnboardingQuestionnaireState>) {
  addSendOnboardingQuestionnaireCases(builder);
  addSubmitOnboardingQuestionnaireCases(builder);
  addGetOnboardingQuestionnairesByProviderIdCases(builder);
  addGetOnboardingQuestionnairesCases(builder);
  addGetOnboardingQuestionnaireByIdCases(builder);
  addOnboardingGeneralCases(builder, extraActions);
  addOnboardingInsuranceCases(builder, extraActions);
  addOnboardingReferralCases(builder, extraActions);
  addOnboardingAgreementCases(builder, extraActions);
  addOnboardingDocumentsCases(builder, extraActions);
}

function addSendOnboardingQuestionnaireCases(
  builder: ActionReducerMapBuilder<OnboardingQuestionnaireState>,
) {
  const { sendOnboardingQuestionnaire } = extraActions;

  builder
    .addCase(sendOnboardingQuestionnaire.pending, (state: OnboardingQuestionnaireState) => {
      state.sendQuestionnaireLoading = true;
      state.error = null;
    })
    .addCase(
      sendOnboardingQuestionnaire.fulfilled,
      (
        state: OnboardingQuestionnaireState,
        action: PayloadAction<IOnboardingQuestionnaireBasic>,
      ) => {
        state.sendQuestionnaireLoading = false;
        if (state.onboardingQuestionnairesBasicInfoProviderId === action.payload.provider_id) {
          state.onboardingQuestionnairesBasicInfo.push(action.payload);
        }
        state.onboardingQuestionnaires.push(action.payload);
        toast.success('Onboarding questionnaire sent successfully');
      },
    )
    .addCase(
      sendOnboardingQuestionnaire.rejected,
      (state: OnboardingQuestionnaireState, action) => {
        state.error = action.error;
        state.sendQuestionnaireLoading = false;
        toast.error(getSerializedErrorMessage(action.error));
      },
    );
}

function addSubmitOnboardingQuestionnaireCases(
  builder: ActionReducerMapBuilder<OnboardingQuestionnaireState>,
) {
  const { submitOnboardingQuestionnaire } = extraActions;

  builder
    .addCase(submitOnboardingQuestionnaire.pending, (state: OnboardingQuestionnaireState) => {
      state.submitQuestionnaireLoading = true;
      state.error = null;
    })
    .addCase(
      submitOnboardingQuestionnaire.fulfilled,
      (
        state: OnboardingQuestionnaireState,
        action: PayloadAction<IOnboardingQuestionnaireBasic>,
      ) => {
        state.submitQuestionnaireLoading = false;
        // Submit returns basic metadata only; patch conclude fields so nested form data stays intact.
        if (state.onboardingQuestionnaireDetails?.id === action.payload.id) {
          state.onboardingQuestionnaireDetails = patchOnboardingQuestionnaireConcludeFields(
            state.onboardingQuestionnaireDetails,
            action.payload,
          );
        }
        const basicInfoIndex = state.onboardingQuestionnairesBasicInfo.findIndex(
          (row) => row.id === action.payload.id,
        );
        if (basicInfoIndex !== -1) {
          state.onboardingQuestionnairesBasicInfo[basicInfoIndex] =
            patchOnboardingQuestionnaireConcludeFields(
              state.onboardingQuestionnairesBasicInfo[basicInfoIndex],
              action.payload,
            );
        }
        const historyIndex = state.onboardingQuestionnaires.findIndex(
          (row) => row.id === action.payload.id,
        );
        if (historyIndex !== -1) {
          state.onboardingQuestionnaires[historyIndex] = patchOnboardingQuestionnaireConcludeFields(
            state.onboardingQuestionnaires[historyIndex],
            action.payload,
          );
        }
        toast.success('Onboarding questionnaire submitted successfully');
      },
    )
    .addCase(
      submitOnboardingQuestionnaire.rejected,
      (state: OnboardingQuestionnaireState, action) => {
        state.error = action.error;
        state.submitQuestionnaireLoading = false;
        toast.error(getSerializedErrorMessage(action.error), { toastId: 'global-toast-error' });
      },
    );
}

function addGetOnboardingQuestionnairesByProviderIdCases(
  builder: ActionReducerMapBuilder<OnboardingQuestionnaireState>,
) {
  const { getOnboardingQuestionnairesByProviderId } = extraActions;

  builder
    .addCase(
      getOnboardingQuestionnairesByProviderId.pending,
      (state: OnboardingQuestionnaireState, action) => {
        state.onboardingQuestionnairesBasicInfo = [];
        state.onboardingQuestionnairesBasicInfoLoading = true;
        state.onboardingQuestionnairesBasicInfoRequestId = action.meta.requestId;
        state.onboardingQuestionnairesBasicInfoProviderId = action.meta.arg.providerId;
        state.error = null;
      },
    )
    .addCase(
      getOnboardingQuestionnairesByProviderId.fulfilled,
      (state: OnboardingQuestionnaireState, action) => {
        if (action.meta.requestId !== state.onboardingQuestionnairesBasicInfoRequestId) return;
        state.onboardingQuestionnairesBasicInfoLoading = false;
        state.onboardingQuestionnairesBasicInfo = action.payload;
      },
    )
    .addCase(
      getOnboardingQuestionnairesByProviderId.rejected,
      (state: OnboardingQuestionnaireState, action) => {
        if (action.meta.requestId !== state.onboardingQuestionnairesBasicInfoRequestId) return;
        if (action.meta.aborted) return;
        state.error = action.error;
        state.onboardingQuestionnairesBasicInfoLoading = false;
        toast.error(getSerializedErrorMessage(action.error));
      },
    );
}

function addGetOnboardingQuestionnairesCases(
  builder: ActionReducerMapBuilder<OnboardingQuestionnaireState>,
) {
  const { getOnboardingQuestionnaires } = extraActions;

  builder
    .addCase(getOnboardingQuestionnaires.pending, (state: OnboardingQuestionnaireState) => {
      state.onboardingQuestionnairesLoading = true;
      state.error = null;
    })
    .addCase(
      getOnboardingQuestionnaires.fulfilled,
      (
        state: OnboardingQuestionnaireState,
        action: PayloadAction<IOnboardingQuestionnaireBasic[]>,
      ) => {
        state.onboardingQuestionnairesLoading = false;
        state.onboardingQuestionnaires = action.payload;
      },
    )
    .addCase(
      getOnboardingQuestionnaires.rejected,
      (state: OnboardingQuestionnaireState, action) => {
        if (action.meta.aborted) return;
        state.error = action.error;
        state.onboardingQuestionnairesLoading = false;
        toast.error(getSerializedErrorMessage(action.error));
      },
    );
}

function addGetOnboardingQuestionnaireByIdCases(
  builder: ActionReducerMapBuilder<OnboardingQuestionnaireState>,
) {
  const { getOnboardingQuestionnaireById } = extraActions;

  builder
    .addCase(
      getOnboardingQuestionnaireById.pending,
      (state: OnboardingQuestionnaireState, action) => {
        // Drop stale details from a previously opened questionnaire.
        if (
          state.onboardingQuestionnaireDetails &&
          String(state.onboardingQuestionnaireDetails.id) !==
            action.meta.arg.onboardingQuestionnaireId
        ) {
          state.onboardingQuestionnaireDetails = null;
        }
        state.questionnaireDetailsLoading = true;
        state.questionnaireDetailsRequestId = action.meta.requestId;
        state.error = null;
      },
    )
    .addCase(
      getOnboardingQuestionnaireById.fulfilled,
      (state: OnboardingQuestionnaireState, action) => {
        if (action.meta.requestId !== state.questionnaireDetailsRequestId) return;
        state.questionnaireDetailsLoading = false;
        const existingDocuments = state.onboardingQuestionnaireDetails?.documents;
        state.onboardingQuestionnaireDetails = action.payload;
        // GET returns document metadata without signed URLs; keep any already-fetched URLs.
        if (!existingDocuments?.length) {
          return;
        }
        state.onboardingQuestionnaireDetails.documents =
          mergeOnboardingQuestionnaireDocumentsPreservingUrls(
            existingDocuments,
            action.payload.documents ?? existingDocuments,
          );
      },
    )
    .addCase(
      getOnboardingQuestionnaireById.rejected,
      (state: OnboardingQuestionnaireState, action) => {
        if (action.meta.requestId !== state.questionnaireDetailsRequestId) return;
        state.error = action.error;
        state.questionnaireDetailsLoading = false;
        toast.error(getSerializedErrorMessage(action.error));
        redirectToDashboardOnClientError(action.error.code);
      },
    );
}

export const onboardingQuestionnaireActions = { ...slice.actions, ...extraActions };
export const onboardingQuestionnaireReducer = slice.reducer;
